import { useState } from "react";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBolt,
  faPenToSquare,
  faTrashCan,
  faCheck,
  faXmark
} from "@fortawesome/free-solid-svg-icons";

import { hexToRgba } from "@aclymatepackages/converters";

const TemplateRow = ({ template, onRename, onDelete }) => {
  const { palette } = useTheme();
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(template.name || "");

  const commitRename = async () => {
    const trimmed = draftName.trim();
    if (!trimmed || trimmed === template.name) {
      setEditing(false);
      return;
    }
    await onRename({ ...template, name: trimmed });
    setEditing(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.5,
        borderRadius: 1,
        backgroundColor: hexToRgba(palette.secondary.main, 0.06),
        border: `1px solid ${palette.backgroundGray.main}`
      }}
    >
      <FontAwesomeIcon icon={faBolt} style={{ color: palette.secondary.main }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {editing ? (
          <TextField
            size="small"
            value={draftName}
            onChange={(event) => setDraftName(event.target.value)}
            // eslint-disable-next-line jsx-a11y/no-autofocus -- intentional: focus the field the user is about to type into
            autoFocus
            fullWidth
          />
        ) : (
          <Typography variant="subtitle2" noWrap>
            {template.name || "(unnamed format)"}
          </Typography>
        )}
        <Typography variant="caption" color="textSecondary">
          {template.layout || "unknown layout"} ·{" "}
          {Object.keys(template.materialTypeOverrides || {}).length} learned
          materials
        </Typography>
      </Box>
      {editing ? (
        <>
          <Tooltip title="Save name">
            <IconButton size="small" onClick={commitRename}>
              <FontAwesomeIcon icon={faCheck} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancel">
            <IconButton size="small" onClick={() => setEditing(false)}>
              <FontAwesomeIcon icon={faXmark} />
            </IconButton>
          </Tooltip>
        </>
      ) : (
        <>
          <Tooltip title="Rename">
            <IconButton size="small" onClick={() => setEditing(true)}>
              <FontAwesomeIcon icon={faPenToSquare} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete saved format">
            <IconButton
              size="small"
              onClick={() => onDelete(template)}
              sx={{ color: palette.error.main }}
            >
              <FontAwesomeIcon icon={faTrashCan} />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Box>
  );
};

const BomTemplatesManager = ({
  open,
  onClose,
  templates,
  onSaveTemplate,
  onDeleteTemplate
}) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
    <DialogTitle>Saved BOM formats</DialogTitle>
    <DialogContent>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Each saved format remembers how to parse a customer&apos;s BOM template
        — including the materials you confirmed. BOMs matching a saved format
        parse instantly with no AI cost. Rename to keep your library organized,
        or delete to re-train on the next upload of that format.
      </Typography>
      {templates.length === 0 ? (
        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
          No formats saved yet. Upload a BOM and confirm the extraction to save
          your first format.
        </Typography>
      ) : (
        <Stack spacing={1} sx={{ mt: 2 }}>
          {templates.map((template) => (
            <TemplateRow
              key={template.id}
              template={template}
              onRename={onSaveTemplate}
              onDelete={onDeleteTemplate}
            />
          ))}
        </Stack>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
);

export default BomTemplatesManager;
